import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';
/** UI density: rendered via `<html>`'s root `font-size` (`App.tsx`'s
 *  `FONT_SIZE_FOR`) — only `rem`-sized CSS scales with it; still-`px` values
 *  don't. Was a `zoom` factor on `<body>` (scaled everything, `px` included)
 *  until 2026-09-25, changed for a scroll-leak bug `zoom` contributed to. */
export type FontScale = 'sm' | 'md' | 'lg';

interface ThemeState {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  fontScale: FontScale;
}

interface ThemeActions {
  setPreference: (p: ThemePreference) => void;
  setFontScale: (f: FontScale) => void;
  /** Called internally when system preference changes */
  _resolveSystem: () => void;
}

function resolveTheme(preference: ThemePreference | undefined): ResolvedTheme {
  if (preference === 'light') return 'light';
  if (preference === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  // 'dark' or anything unset/invalid → dark-first (matches the prototype + design intent).
  return 'dark';
}

export const useThemeStore = create<ThemeState & ThemeActions>()(
  persist(
    // Dark-first operator console (matches the prototype + design intent). Users can still
    // pick light or system via the theme toggle; the choice is persisted.
    (set, get) => ({
      preference: 'dark',
      resolved: resolveTheme('dark'),
      fontScale: 'md',

      setPreference: (preference) => set({ preference, resolved: resolveTheme(preference) }),

      setFontScale: (fontScale) => set({ fontScale }),

      _resolveSystem: () => {
        const { preference } = get();
        if (preference === 'system') {
          set({ resolved: resolveTheme('system') });
        }
      },
    }),
    {
      name: 'xantory:theme',
      partialize: (s) => ({ preference: s.preference, fontScale: s.fontScale }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        if (state.preference !== 'light' && state.preference !== 'dark' && state.preference !== 'system') {
          state.preference = 'dark';
        }
        if (state.fontScale !== 'sm' && state.fontScale !== 'md' && state.fontScale !== 'lg') {
          state.fontScale = 'md';
        }
        state.resolved = resolveTheme(state.preference);
      },
    },
  ),
);

/** Wire up the system media query listener. Call once at app init. */
export function initThemeListener() {
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = () => useThemeStore.getState()._resolveSystem();
  mq.addEventListener('change', handler);
  return () => mq.removeEventListener('change', handler);
}
